'use client';

import * as TabsPrimitive from '@radix-ui/react-tabs';
import type { VariantProps } from 'class-variance-authority';
import { cva } from 'class-variance-authority';
import * as React from 'react';
import mergeClasses, { cn } from '@/shared/lib/utils';
const Tabs = TabsPrimitive.Root;
const TabsListVariants = cva('inline-flex h-10 items-center justify-center', {
  variants: {
    variant: {
      default: 'rounded-lg bg-accent text-muted-foreground [&_:first-child]:rounded-l-lg [&_:last-child]:rounded-r-lg divide-x divide-popover',
      folder: 'p-0 dark:text-status-warning'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const TabsTriggerVariants = cva('inline-flex items-center justify-center', {
  variants: {
    variant: {
      default: 'whitespace-nowrap h-full px-3 py-1.5 text-sm font-medium underline-offset-8 ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=active]:bg-primary/30  data-[state=active]:text-foreground',
      folder: 'h-full px-3 py-1.5 text-title4 font-medium transition-all data-[state=active]:rounded-t-lg data-[state=active]:bg-secondary-background/30 data-[state=active]:text-foreground disabled:cursor-not-allowed disabled:opacity-50 '
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
const TabsContentVariants = cva('ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2', {
  variants: {
    variant: {
      default: 'mt-2',
      folder: 'w-full rounded-md bg-secondary-background/30 px-6 pb-6 pt-5'
    }
  },
  defaultVariants: {
    variant: 'default'
  }
});
export interface TabsListProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.List>, VariantProps<typeof TabsListVariants> {}
const TabsList = React.forwardRef<React.ElementRef<typeof TabsPrimitive.List>, TabsListProps>(({
  className,
  variant = 'default',
  ...props
}, ref) => <TabsPrimitive.List ref={ref} className={mergeClasses(TabsListVariants({
  variant,
  className
}))} {...props} />);
TabsList.displayName = TabsPrimitive.List.displayName;
interface TabsTriggerProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Trigger>, VariantProps<typeof TabsTriggerVariants> {}
const TabsTrigger = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Trigger>, TabsTriggerProps>(({
  className,
  variant = 'default',
  ...props
}, ref) => <TabsPrimitive.Trigger ref={ref} className={mergeClasses(TabsTriggerVariants({
  variant,
  className
}))} {...props} />);
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName;
interface TabsContentProps extends React.ComponentPropsWithoutRef<typeof TabsPrimitive.Content>, VariantProps<typeof TabsTriggerVariants> {}
const TabsContent = React.forwardRef<React.ElementRef<typeof TabsPrimitive.Content>, TabsContentProps>(({
  className,
  variant,
  ...props
}, ref) => <TabsPrimitive.Content ref={ref} className={cn(TabsContentVariants({
  variant,
  className
}))} forceMount={undefined} {...props} />);
TabsContent.displayName = TabsPrimitive.Content.displayName;
export { Tabs, TabsContent, TabsList, TabsTrigger };